import { base64Decode } from "./encoding";
import { parseField } from "./field";
import type {
  ChainNoteRecord,
  ChainNullifierRecord,
  RecoverySource,
} from "./recovery";

export type SmartQueryClient = {
  queryContractSmart<T>(
    contract: string,
    query: Record<string, unknown>
  ): Promise<T>;
};

type TreeResponse = {
  next_index: number;
  next_nullifier_index: number;
  root: string;
  root_sequence: number;
  depth: number;
};

const safeInteger = (value: unknown, label: string): number => {
  const parsed =
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  if (!Number.isSafeInteger(parsed) || Number(parsed) < 0) {
    throw new Error(`Invalid ${label} in Private Mode response`);
  }
  return Number(parsed);
};

const parseTree = (value: unknown): TreeResponse => {
  if (!value || typeof value !== "object")
    throw new Error("Invalid Private Mode tree response");
  const tree = value as Record<string, unknown>;
  const depth = safeInteger(tree.depth, "tree depth");
  if (depth !== 32) throw new Error("Unsupported Private Mode tree depth");
  if (typeof tree.root !== "string")
    throw new Error("Invalid Private Mode tree root");
  parseField(tree.root);
  return {
    next_index: safeInteger(tree.next_index, "next note index"),
    next_nullifier_index: safeInteger(
      tree.next_nullifier_index,
      "next nullifier index"
    ),
    root: tree.root,
    root_sequence: safeInteger(tree.root_sequence, "root sequence"),
    depth,
  };
};

const parseNote = (value: unknown): ChainNoteRecord => {
  if (!value || typeof value !== "object")
    throw new Error("Invalid Private Mode note response");
  const note = value as Record<string, unknown>;
  if (note.kind !== "reserve" && note.kind !== "control") {
    throw new Error("Invalid Private Mode note kind");
  }
  if (typeof note.commitment !== "string")
    throw new Error("Invalid Private Mode commitment");
  parseField(note.commitment);
  if (typeof note.encrypted_note !== "string") {
    throw new Error("Invalid Private Mode encrypted note");
  }
  base64Decode(note.encrypted_note);
  return {
    index: safeInteger(note.index, "note index"),
    commitment: note.commitment,
    kind: note.kind,
    encrypted_note: note.encrypted_note,
    block_height: safeInteger(note.block_height, "note block height"),
  };
};

const parseNotes = (value: unknown): { notes: ChainNoteRecord[] } => {
  if (
    !value ||
    typeof value !== "object" ||
    !Array.isArray((value as { notes?: unknown }).notes)
  ) {
    throw new Error("Invalid Private Mode notes response");
  }
  return { notes: (value as { notes: unknown[] }).notes.map(parseNote) };
};

const parseNullifier = (value: unknown): ChainNullifierRecord => {
  if (!value || typeof value !== "object") {
    throw new Error("Invalid Private Mode nullifier response");
  }
  const record = value as Record<string, unknown>;
  if (typeof record.nullifier !== "string") {
    throw new Error("Invalid Private Mode nullifier");
  }
  parseField(record.nullifier);
  return {
    index: safeInteger(record.index, "nullifier index"),
    nullifier: record.nullifier,
  };
};

const parseNullifiers = (
  value: unknown
): { nullifiers: ChainNullifierRecord[] } => {
  if (
    !value ||
    typeof value !== "object" ||
    !Array.isArray((value as { nullifiers?: unknown }).nullifiers)
  ) {
    throw new Error("Invalid Private Mode nullifiers response");
  }
  return {
    nullifiers: (value as { nullifiers: unknown[] }).nullifiers.map(
      parseNullifier
    ),
  };
};

export const createContractRecoverySource = (
  client: SmartQueryClient,
  proxyContract: string
): RecoverySource => ({
  async fetchNotes(startAfter, limit) {
    return parseNotes(
      await client.queryContractSmart(proxyContract, {
        notes: { start_after: startAfter, limit },
      })
    );
  },
  async fetchNullifiers(startAfter, limit) {
    return parseNullifiers(
      await client.queryContractSmart(proxyContract, {
        nullifiers: { start_after: startAfter, limit },
      })
    );
  },
  async snapshot() {
    const tree = parseTree(
      await client.queryContractSmart(proxyContract, { tree: {} })
    );
    return {
      nextIndex: tree.next_index,
      nextNullifierIndex: tree.next_nullifier_index,
      root: tree.root,
    };
  },
});

/**
 * Recovery reads the complete authoritative contract pages directly.
 *
 * Comparing a cache page against that same contract page cannot save a chain
 * query. Fetching both only adds a disposable service dependency, including
 * hangs while waiting for response headers or JSON. Do not start that request:
 * notes, ciphertext, nullifiers and the snapshot all remain contract-sourced,
 * and an unavailable or invalid contract response must still fail closed.
 *
 * Keep the existing options for constructor compatibility. The backend can
 * still serve other clients and relay transactions; it is not a recovery
 * dependency. An authenticated cache optimization needs a separate design
 * before it can replace any of these authoritative reads.
 */
export const createResilientRecoverySource = (params: {
  contract: RecoverySource;
  backendUrl?: string;
  fetchImpl?: typeof fetch;
}): RecoverySource => params.contract;
